import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { supabase } from "./supabase";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Only log in development
  if (import.meta.env.DEV) {
    console.log(`API Request: ${method} ${url}`, data);
  }
  
  // Get the Supabase session token
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  
  // Create request options
  const options: RequestInit = {
    method,
    credentials: "include",
    headers: { 
      "Authorization": token ? `Bearer ${token}` : '',
      "Content-Type": "application/json" 
    }
  };
  
  // Add body if there's data
  if (data) {
    options.body = JSON.stringify(data);
  }
  
  if (import.meta.env.DEV) {
    console.log("Request options:", options);
  }
  
  try {
    const res = await fetch(url, options);
    if (import.meta.env.DEV) {
      console.log(`Response status: ${res.status}`, res);
    }
    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("API request error:", error);
    }
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    if (import.meta.env.DEV) {
      console.log(`Query fetch: ${queryKey[0]}`);
    }
    try {
      // Get the Supabase session token
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      // Create headers with authorization token
      const headers: HeadersInit = {
        "Authorization": token ? `Bearer ${token}` : '',
        "Content-Type": "application/json"
      };
      
      const res = await fetch(queryKey[0] as string, {
        credentials: "include",
        headers
      });
      
      if (import.meta.env.DEV) {
        console.log(`Query response status: ${res.status}`);
      }
      
      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        if (import.meta.env.DEV) {
          console.log("Unauthorized (401), returning null as configured");
        }
        return null;
      }

      await throwIfResNotOk(res);
      const data = await res.json();
      if (import.meta.env.DEV) {
        console.log("Query response data:", data);
      }
      return data;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Query error:", error);
      }
      throw error;
    }
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes instead of Infinity for better UX
      gcTime: 10 * 60 * 1000, // 10 minutes garbage collection
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors except 408, 429
        if (error instanceof Error && error.message.includes('4')) {
          const status = parseInt(error.message.split(':')[0]);
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
          }
        }
        return failureCount < 2; // Retry up to 2 times
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: false,
    },
  },
});
